<template>
  <v-container fluid class="pa-4 mdfe-distribuicao">
    <v-card outlined>
      <v-toolbar flat dense color="#1d4ed8" dark>
        <v-icon left>mdi-truck-delivery-outline</v-icon>
        <v-toolbar-title class="subtitle-1 font-weight-medium">
          MDF-e de Distribuição
        </v-toolbar-title>
      </v-toolbar>

      <div class="pa-4">
        <!-- ============ RODADA ============ -->
        <v-sheet outlined rounded class="pa-3 mb-4 grey lighten-5">
          <div class="bloco-titulo">Rodada de distribuição</div>
          <v-row dense align="center">
            <v-col cols="12" md="5">
              <v-select v-model="idParticipante" :items="participantes" item-text="nome"
                item-value="id" label="Transportador" outlined hide-details dense
                :loading="carregandoParticipantes" @change="trocarTransportador" />
            </v-col>
            <!--
              Com rodada aberta o periodo nao tem efeito: a rodada ja esta
              congelada. Ficam desabilitados em vez de recarregarem e
              apagarem a lista da rodada na cara do operador.
            -->
            <v-col cols="6" md="2">
              <v-text-field v-model="inicio" type="date" label="De" outlined hide-details dense
                :disabled="emRodada" @change="carregarEncerrados" />
            </v-col>
            <v-col cols="6" md="2">
              <v-text-field v-model="fim" type="date" label="Até" outlined hide-details dense
                :disabled="emRodada" @change="carregarEncerrados" />
            </v-col>
            <v-col cols="12" md="3" class="pt-2 pt-md-0">
              <div v-if="emRodada" class="ajuda">
                Rodada em andamento: o período já está congelado nela. Finalize para
                abrir outra.
              </div>
              <div v-else class="ajuda">
                O período acha o MDF-e que chegou ao CD. Quem decide a lista é o
                MDF-e escolhido abaixo.
              </div>
            </v-col>
          </v-row>
        </v-sheet>

        <div v-if="!idParticipante" class="estado-vazio">
          <v-icon size="44" color="#cbd5e1">mdi-truck-delivery-outline</v-icon>
          <div class="body-2 grey--text mt-2">Escolha o transportador para começar.</div>
        </div>

        <template v-else>
          <!--
            Rodada aberta. Quando existe uma, ela e a tela: o seletor de
            MDF-e sai, porque a rodada ja esta congelada e trocar o
            periodo nao muda nada nela. Para comecar outra, finaliza.
          -->
          <v-sheet v-if="emRodada" outlined rounded class="pa-3 mb-4 rodada-aberta">
            <div class="fila">
              <div>
                <div class="bloco-titulo mb-0">{{ tituloRodada }}</div>
                <div class="ajuda">
                  Você voltou para a rodada em andamento. Ela guarda os CT-e como estavam
                  na abertura; a rota de cada um é resolvida agora, então rota cadastrada
                  depois já aparece agrupada.
                </div>
              </div>
              <v-spacer />
              <v-btn small outlined :loading="finalizando" @click="finalizarRodada">
                <v-icon left small>mdi-check-circle-outline</v-icon>Finalizar rodada
              </v-btn>
            </div>
          </v-sheet>

          <!-- ============ MDF-e ENCERRADOS ============ -->
          <v-sheet v-else outlined rounded class="pa-3 mb-4">
            <div class="fila mb-2">
              <div class="bloco-titulo mb-0">MDF-e encerrados no período</div>
              <v-spacer />
              <span v-if="encerrados.length" class="sub">
                {{ marcados.length }} de {{ encerrados.length }} marcado(s)
              </span>
            </div>

            <div v-if="erroPeriodo" class="aviso erro">
              <span class="ic">⚠️</span><div>{{ erroPeriodo }}</div>
            </div>

            <div v-else-if="erroEncerrados" class="aviso erro">
              <span class="ic">⚠️</span>
              <div>
                <strong>Não foi possível carregar os MDF-e.</strong> {{ erroEncerrados }}
                <a class="lnk" @click="carregarEncerrados">tentar de novo →</a>
              </div>
            </div>

            <div v-else-if="carregandoEncerrados" class="sub">Carregando…</div>

            <div v-else-if="!encerrados.length" class="aviso info">
              <span class="ic">ℹ️</span>
              <div>Nenhum MDF-e encerrado nesse período.</div>
            </div>

            <template v-else>
              <div class="encerrados">
                <label v-for="m in encerrados" :key="m.chaveAcesso" class="enc">
                  <input type="checkbox" :value="m.chaveAcesso" v-model="marcados">
                  <span class="enc-num">{{ m.numeroDocumento }}/{{ m.serie }}</span>
                  <span class="enc-info">
                    {{ data(m.dataEmissao) }} · {{ m.placa || '—' }} ·
                    {{ m.ufInicio }}→{{ m.ufFim }}
                  </span>
                  <span class="b b-c">{{ m.quantidadeCtes }} CT-e</span>
                </label>
              </div>

              <div class="fila mt-3">
                <v-btn color="#1d4ed8" dark small :disabled="!marcados.length"
                  :loading="carregandoLista" @click="buscar">
                  <v-icon left small>mdi-format-list-group</v-icon>Ver CT-e dos marcados
                </v-btn>
                <v-btn small text :loading="carregandoLista" @click="buscarPeriodo">
                  Trazer todos do período
                </v-btn>
              </div>
            </template>
          </v-sheet>

          <!-- ============ LISTA AGRUPADA ============ -->
          <div v-if="erroLista" class="aviso erro">
            <span class="ic">⚠️</span>
            <div>
              <strong>Não foi possível carregar os CT-e.</strong> {{ erroLista }}
              <a class="lnk" @click="buscar">tentar de novo →</a>
            </div>
          </div>

          <template v-else-if="lista">
            <div class="totais">
              <!--
                "CT-e" e nao "CT-e na rodada": desde que a rodada virou
                coisa gravada, chamar o resultado da consulta de rodada
                confundiria os dois.
              -->
              <div class="total az">
                <div class="r">CT-e</div><div class="v">{{ lista.totalCtes }}</div>
              </div>
              <div class="total">
                <div class="r">Peso</div><div class="v">{{ peso(lista.pesoTotal) }}</div>
              </div>
              <div class="total">
                <div class="r">Valor da carga</div>
                <div class="v">{{ moeda(lista.valorCargaTotal) }}</div>
              </div>
              <div class="total" :class="{ vm: lista.cidadesSemAgrupador }">
                <div class="r">Cidades sem rota</div>
                <div class="v">{{ lista.cidadesSemAgrupador }}</div>
              </div>
            </div>

            <!--
              O convite para abrir a rodada vem DEPOIS dos totais, de
              proposito: o operador confere o que esta trazendo e so
              entao grava. Em cima do seletor ele estaria apostando.
            -->
            <div v-if="!emRodada" class="fila mb-3">
              <v-btn color="#1d4ed8" dark small :loading="abrindo" @click="abrirRodada">
                <v-icon left small>mdi-play-circle-outline</v-icon>Abrir rodada com estes CT-e
              </v-btn>
              <span class="ajuda ml-3">
                Grava a rodada. A partir daí ela sobrevive ao fechamento da tela.
              </span>
            </div>

            <div v-if="erroRodada" class="aviso erro">
              <span class="ic">⚠️</span>
              <div>
                <strong>Não foi possível abrir a rodada.</strong> {{ erroRodada }}
              </div>
            </div>

            <!--
              Sem rota cadastrada a lista nao quebra - ela sai por cidade,
              que e o estado do primeiro uso. O convite existe porque 31
              cidades num MDF-e virariam 31 MDF-e novos de 6 CT-e cada.
            -->
            <div v-if="lista.cidadesSemAgrupador" class="aviso info">
              <span class="ic">ℹ️</span>
              <div>
                {{ lista.cidadesSemAgrupador }} cidade(s) ainda não pertencem a nenhuma rota e
                aparecem em <strong>Sem agrupador</strong>.
                <a class="lnk" @click="irParaAgrupador">cadastrar rotas →</a>
              </div>
            </div>

            <!--
              A fila do cadastro. O aviso acima diz que falta rota; esta
              tabela diz em que ordem resolver, que e a parte acionavel.
              Sai do grupo "Sem agrupador" que ja veio na resposta: nao
              custa requisicao nenhuma.
            -->
            <v-sheet v-if="fila.length" outlined rounded class="pa-3 mb-3 grey lighten-5 fila-cadastro">
              <div class="bloco-titulo">Fila do cadastro de rotas</div>
              <div class="ajuda mb-2">
                Em ordem de volume. As <strong>{{ cidadesDoAlvo }}</strong> primeiras cobrem
                {{ alvo }}% dos CT-e sem rota — a cauda de 1 e 2 CT-e pode ficar sem rota
                sem prejuízo.
              </div>
              <v-simple-table dense class="tabela">
                <thead>
                  <tr>
                    <th>#</th><th>UF</th><th>Cidade</th>
                    <th class="text-end">CT-e</th>
                    <th class="text-end">Peso</th>
                    <th class="text-end">Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(c, i) in filaVisivel"
                    :key="'f' + i"
                    :class="{ dentro: c.acumuladoPct <= alvo }"
                  >
                    <td class="sub">{{ i + 1 }}</td>
                    <td>{{ c.uf || '—' }}</td>
                    <td>
                      {{ c.municipio || '—' }}
                      <div v-if="!c.codigoIbge" class="sub alerta">sem código IBGE</div>
                    </td>
                    <td class="text-end font-weight-medium">{{ c.ctes }}</td>
                    <td class="text-end">{{ peso(c.peso) }}</td>
                    <td class="text-end sub">{{ c.acumuladoPct }}%</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div class="fila mt-2">
                <a v-if="filaCortada && !filaInteira" class="lnk" @click="filaInteira = true">
                  ver as {{ fila.length }} cidades →
                </a>
                <a v-else-if="filaCortada" class="lnk" @click="filaInteira = false">
                  mostrar menos
                </a>
                <v-spacer />
                <a class="lnk" @click="irParaAgrupador">cadastrar rotas →</a>
              </div>
            </v-sheet>

            <div v-if="!lista.grupos.length" class="estado-vazio">
              <v-icon size="44" color="#cbd5e1">mdi-package-variant</v-icon>
              <div class="body-2 grey--text mt-2">
                Nenhum CT-e encontrado nos MDF-e selecionados.
              </div>
            </div>

            <v-expansion-panels v-else v-model="abertos" multiple accordion flat>
              <v-expansion-panel v-for="(g, i) in lista.grupos" :key="'g' + i" class="grupo">
                <v-expansion-panel-header>
                  <div class="cab">
                    <span class="nome" :class="{ sem: !g.idAgrupador }">{{ g.nome }}</span>
                    <span class="sub">{{ resumo(g) }}</span>
                    <v-spacer />
                    <span class="num">{{ peso(g.peso) }}</span>
                    <span class="num forte">{{ moeda(g.valorCarga) }}</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-simple-table dense class="tabela">
                    <thead>
                      <tr>
                        <th>CT-e</th><th>Destino</th><th>Destinatário</th>
                        <th class="text-end">Peso</th><th class="text-end">Valor</th>
                        <th>Veio no MDF-e</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in g.ctes" :key="c.chaveAcesso">
                        <td>
                          <div class="font-weight-medium">{{ c.numeroDocumento }}/{{ c.serie }}</div>
                          <div class="sub">{{ data(c.dataEmissao) }}</div>
                        </td>
                        <td>
                          {{ c.municipio || '—' }}<span v-if="c.uf"> — {{ c.uf }}</span>
                          <div v-if="!c.codigoIbge" class="sub alerta">sem código IBGE</div>
                        </td>
                        <td>
                          {{ c.destinatario || '—' }}
                          <div class="sub">{{ c.documentoDestinatario || '' }}</div>
                        </td>
                        <td class="text-end">{{ peso(c.peso) }}</td>
                        <td class="text-end">{{ moeda(c.valorCarga) }}</td>
                        <td class="sub">{{ c.mdfeOrigemNumero || '—' }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </template>
      </div>
    </v-card>

    <v-snackbar v-model="snack.on" :color="snack.cor" :timeout="5000" bottom>
      {{ snack.txt }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snack.on = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import MdfeDistribuicaoService from '../service/mdfeDistribuicao'
import Transportador from '../service/transportador'
import {
  periodoPadrao, erroDoPeriodo, formatarPeso, formatarMoeda, formatarData, resumoDoGrupo,
  filaDeCadastro, cidadesParaCobrir, rodadaEmAndamento, resumoDaRodada,
  COBERTURA_ALVO, FILA_VISIVEL
} from '../utils/mdfeDistribuicao'

/**
 * MDF-e de distribuição — leitura da rodada.
 *
 * O transportador recebe a carga no CD dentro de MDF-e encerrados e
 * remonta MDF-e por região para a perna final. Esta tela mostra o que
 * chegou, agrupado pelas rotas que ele cadastrou no Agrupador de Rotas.
 *
 * Dois caminhos, e a diferença entre eles importa:
 *
 *   consulta — lê e não grava. Serve para conferir o que chegou contra o
 *     papel e contra o outro sistema, e pode ser repetida à vontade.
 *   rodada  — grava. Congela os CT-e que a consulta mostrou, sobrevive ao
 *     fechamento da tela, e é em cima dela que a emissão vai acontecer.
 *
 * Só existe uma rodada aberta por transportador. Ao abrir a tela, se há
 * rodada em andamento é ela que aparece: o operador volta para onde
 * parou em vez de refazer a consulta.
 *
 * Diferente dos relatórios, a tela NÃO abre com a lista carregada. Ela
 * carrega só os MDF-e encerrados do período — que são poucos — e espera o
 * operador escolher. Um dia de pico tem 500 CT-e, e o caminho normal é
 * partir do MDF-e que chegou com a carga.
 *
 * Ainda não emite documento nem consome numeração.
 */
export default {
  name: 'MdfeDistribuicao',

  data () {
    const periodo = periodoPadrao()
    return {
      participantes: [],
      idParticipante: null,
      carregandoParticipantes: false,

      inicio: periodo.inicio,
      fim: periodo.fim,

      encerrados: [],
      marcados: [],
      carregandoEncerrados: false,
      erroEncerrados: '',

      lista: null,
      abertos: [],
      carregandoLista: false,
      erroLista: '',
      filaInteira: false,
      alvo: COBERTURA_ALVO,

      rodada: null,
      abrindo: false,
      finalizando: false,
      erroRodada: '',

      snack: { on: false, txt: '', cor: 'success' }
    }
  },

  computed: {
    erroPeriodo () {
      return erroDoPeriodo(this.inicio, this.fim)
    },

    fila () {
      return filaDeCadastro(this.lista)
    },

    filaCortada () {
      return this.fila.length > FILA_VISIVEL
    },

    filaVisivel () {
      return this.filaInteira ? this.fila : this.fila.slice(0, FILA_VISIVEL)
    },

    cidadesDoAlvo () {
      return cidadesParaCobrir(this.fila, COBERTURA_ALVO)
    },

    emRodada () {
      return rodadaEmAndamento(this.rodada)
    },

    tituloRodada () {
      return resumoDaRodada(this.rodada)
    }
  },

  mounted () {
    const daUrl = Number(this.$route.query.idParticipante)
    if (daUrl) this.idParticipante = daUrl
    this.carregarParticipantes()
  },

  methods: {
    aviso (txt, cor = 'success') {
      this.snack = { on: true, txt, cor }
    },

    async carregarParticipantes () {
      this.carregandoParticipantes = true
      try {
        const r = await Transportador.getTransportadoresUsuario(localStorage.getItem('id'))
        this.participantes = (r.data || [])
          .map(v => v.participante || v)
          .filter(p => p && p.id)
          .map(p => ({ id: p.id, nome: p.razaoSocial || p.nomeFantasia || ('Participante #' + p.id) }))
        // Um transportador so: nao faz o usuario escolher o obvio.
        if (!this.idParticipante && this.participantes.length === 1) {
          this.idParticipante = this.participantes[0].id
        }
        if (this.idParticipante) this.iniciar()
      } catch (e) {
        this.aviso('Não foi possível carregar os transportadores.', 'error')
      } finally {
        this.carregandoParticipantes = false
      }
    },

    trocarTransportador () {
      this.encerrados = []
      this.marcados = []
      this.lista = null
      this.rodada = null
      this.erroRodada = ''
      this.iniciar()
    },

    //
    // A rodada aberta vem primeiro. Se existe uma, ela e a tela, e nao
    // vale gastar uma consulta de MDF-e encerrados que o operador nao vai
    // usar. Falha ao consultar a rodada nao bloqueia: cai no caminho da
    // consulta, que e o de antes.
    //
    async iniciar () {
      const achou = await this.carregarRodadaAberta()
      if (!achou) this.carregarEncerrados()
    },

    async carregarRodadaAberta () {
      if (!this.idParticipante) return false
      this.carregandoLista = true
      try {
        const r = await MdfeDistribuicaoService.rodadaAberta(this.idParticipante)
        // 204 chega como data vazia; rodadaEmAndamento trata os dois.
        this.aplicarRodada(r.data)
        return this.emRodada
      } catch (e) {
        this.rodada = null
        return false
      } finally {
        this.carregandoLista = false
      }
    },

    async abrirRodada () {
      this.abrindo = true
      this.erroRodada = ''
      try {
        const r = await MdfeDistribuicaoService.abrirRodada(
          this.idParticipante, this.inicio, this.fim, this.marcados)
        this.aplicarRodada(r.data)
        this.aviso(this.rodada && this.rodada.reaproveitada
          ? 'Já havia uma rodada aberta — é ela que está na tela.'
          : 'Rodada aberta.')
      } catch (e) {
        this.erroRodada = this.msgErro(e)
      } finally {
        this.abrindo = false
      }
    },

    async finalizarRodada () {
      if (!this.emRodada) return
      this.finalizando = true
      try {
        await MdfeDistribuicaoService.finalizarRodada(this.rodada.idLote, this.idParticipante)
        this.rodada = null
        this.lista = null
        this.marcados = []
        this.aviso('Rodada finalizada.')
        this.carregarEncerrados()
      } catch (e) {
        this.aviso('Não foi possível finalizar a rodada. ' + this.msgErro(e), 'error')
      } finally {
        this.finalizando = false
      }
    },

    //
    // A lista da tela passa a ser a da rodada. O resto do template nao
    // muda: totais, fila do cadastro e grupos ja saem de `lista`.
    //
    aplicarRodada (dados) {
      const rodada = rodadaEmAndamento(dados) ? dados : null
      this.rodada = rodada
      if (!rodada) return

      this.lista = rodada.lista || null
      this.filaInteira = false
      this.abertos = this.lista && this.lista.grupos && this.lista.grupos.length === 1 ? [0] : []
    },

    async carregarEncerrados () {
      if (!this.idParticipante || this.erroPeriodo) return
      this.carregandoEncerrados = true
      this.erroEncerrados = ''
      this.marcados = []
      this.lista = null
      try {
        const r = await MdfeDistribuicaoService.listarMdfeEncerrados(
          this.idParticipante, this.inicio, this.fim)
        this.encerrados = r.data || []
      } catch (e) {
        this.encerrados = []
        this.erroEncerrados = this.msgErro(e)
      } finally {
        this.carregandoEncerrados = false
      }
    },

    buscar () {
      if (!this.marcados.length) return
      this.consultar(this.marcados)
    },

    //
    // Caminho da excecao: sem MDF-e marcado, o periodo inteiro. E o que a
    // API limita a 31 dias, porque um mes de pico sao milhares de CT-e.
    //
    buscarPeriodo () {
      if (this.erroPeriodo) {
        this.aviso(this.erroPeriodo, 'error')
        return
      }
      this.marcados = []
      this.consultar([])
    },

    async consultar (chaves) {
      this.carregandoLista = true
      this.erroLista = ''
      this.filaInteira = false
      try {
        const r = await MdfeDistribuicaoService.listar(
          this.idParticipante, this.inicio, this.fim, chaves)
        this.lista = r.data || null
        // Grupo unico ja abre: nao faz sentido um clique a mais.
        this.abertos = this.lista && this.lista.grupos && this.lista.grupos.length === 1 ? [0] : []
      } catch (e) {
        this.lista = null
        this.erroLista = this.msgErro(e)
      } finally {
        this.carregandoLista = false
      }
    },

    irParaAgrupador () {
      this.$router.push({
        name: 'RotaAgrupador',
        query: { idParticipante: this.idParticipante }
      }).catch(() => {})
    },

    msgErro (e) {
      const d = e && e.response && e.response.data
      return (d && (d.mensagem || d.message || d.erro)) ||
        (e && e.message) || 'erro desconhecido'
    },

    resumo (grupo) { return resumoDoGrupo(grupo) },
    peso (v) { return formatarPeso(v) },
    moeda (v) { return formatarMoeda(v) },
    data (v) { return formatarData(v) }
  }
}
</script>

<style scoped>
/*
  Modelo de tela novo (mesmo de RotaAgrupador). O App.vue tem
  #app { text-align: center }, que desce por heranca - dai o text-align
  no container e as classes proprias no lugar do d-flex, que nesta arvore
  chega sobrescrito para block.
*/
.mdfe-distribuicao { text-align: left; }
.fila { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.bloco-titulo {
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .4px; color: #475569; margin-bottom: 10px;
}
.ajuda { font-size: 11.5px; color: #94a3b8; line-height: 1.45; }
.sub { font-size: 11.5px; color: #94a3b8; }
.sub.alerta { color: #b45309; }
.estado-vazio {
  text-align: center; padding: 48px 16px; border: 1px dashed #e2e8f0;
  border-radius: 10px; background: #f8fafc;
}

.encerrados { display: flex; flex-direction: column; gap: 6px; max-height: 260px; overflow: auto; }
.enc {
  display: flex; align-items: center; gap: 9px; padding: 7px 9px;
  border: 1px solid #e2e8f0; border-radius: 7px; cursor: pointer; font-size: 12.5px;
}
.enc:hover { border-color: #94a3b8; }
.enc-num { font-weight: 700; min-width: 86px; }
.enc-info { color: #475569; flex: 1; }

.totais { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
.total { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; background: #fff; }
.total .r {
  font-size: 10.5px; color: #94a3b8; font-weight: 600;
  text-transform: uppercase; letter-spacing: .3px;
}
.total .v { font-size: 18px; font-weight: 700; margin-top: 2px; }
.total.az .v { color: #1e3a8a; }
.total.vm .v { color: #991b1b; }

.b {
  display: inline-flex; align-items: center; font-size: 10.5px; font-weight: 700;
  padding: 3px 9px; border-radius: 99px; white-space: nowrap;
}
.b-c { background: #e2e8f0; color: #475569; }

.aviso {
  display: flex; gap: 9px; padding: 11px 13px; border-radius: 6px;
  font-size: 12.5px; line-height: 1.45; margin-bottom: 12px; border-left: 3px solid;
}
.aviso .ic { flex-shrink: 0; }
.aviso.info { background: #eff6ff; border-color: #1d4ed8; color: #1e3a8a; }
.aviso.erro { background: #fef2f2; border-color: #dc2626; color: #7f1d1d; }
.aviso .lnk { color: inherit; font-weight: 700; text-decoration: underline; cursor: pointer; }

.grupo { border: 1px solid #e2e8f0; border-radius: 10px !important; margin-bottom: 8px; }
.cab { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; width: 100%; }
.cab .nome { font-weight: 650; font-size: 14px; }
.cab .nome.sem { color: #b45309; }
.cab .num { font-size: 12.5px; color: #475569; }
.cab .num.forte { font-weight: 700; color: #0f172a; }

.tabela >>> th { white-space: nowrap; font-size: 11.5px !important; }
.tabela >>> td { font-size: 12.5px; }

/* As cidades dentro do alvo de cobertura: sao as que valem cadastrar. */
.fila-cadastro >>> tr.dentro { background: #eff6ff; }

/*
  A rodada aberta e um estado, nao um aviso: borda a esquerda em vez de
  fundo colorido, que ja e usado pela fila do cadastro e pelos avisos.
*/
.rodada-aberta { border-left: 3px solid #1d4ed8 !important; }
.lnk { color: #1d4ed8; font-weight: 600; font-size: 12.5px; white-space: nowrap; cursor: pointer; }
.lnk:hover { text-decoration: underline; }

@media (max-width: 960px) {
  .totais { grid-template-columns: repeat(2, 1fr); }
}
</style>
